// V101-3-8 (D-CI-38, D-CI-39): connector-found AI resources.
//
// AI connectors report models, endpoints, agents, vector indexes and guardrails.
// They are not SBOM components, so they are kept as observed resources with an
// ai_role from their kind and the connector's provider_key (migration 319).
//
// A model or vector store first seen AFTER the connector's baseline run, at a
// provider a system's components already use, is a change to that system:
//   - a component-delta check (rule `ai_resource_added`, CM-3, AI-class with the
//     Art. 3(23) "foreseen?" field) through the existing applyDelta path;
//   - in a workspace with a significant-change program (FedRAMP), ONE draft
//     significant change (planned, unclassified by a person, source
//     'component_delta') for a person to classify or cancel.
// Never creates a system; the first run of a connector is a baseline (no deltas).
import { getProvider } from '../connectors/providers.js';
import { programsWhere } from '../programs/manifests.js';
import { applyDelta } from './component-delta-service.js';
import { createSignificantChange } from './significant-change-service.js';
import { listSystemAiResourceDismissals } from './system-ai-resource-dismissal-service.js';
import { syncComponentPresence, upsertConfigurationItem } from './configuration-item-service.js';
/** Inventory kind → component ai_role (null: evidence, not a component). */
export const AI_ROLE_BY_KIND = {
    model: 'model_ref', deployment: 'model_ref', endpoint: 'model_ref',
    agent: 'orchestration', knowledge_base: 'vector_store', vector_index: 'vector_store', guardrail: 'guardrail',
    agent_memory: null, gateway: null, project: null, api_key: null,
};
/** Roles whose arrival is a change worth a delta and a draft significant change. */
const CHANGE_ROLES = new Set(['model_ref', 'vector_store']);
/**
 * Record what a run reported. `complete` (no failed read) lets a resource that is
 * no longer reported be marked gone; a partial run changes nothing (absence is
 * not evidence).
 */
export async function observeAiResources(db, input) {
    const nowIso = input.now.toISOString();
    const existing = (await db.query(`SELECT resource_id, kind, external_id, gone_at, component_ref_id FROM observed_ai_resources WHERE workspace_id = ? AND connector_id = ?`, [input.workspace_id, input.connector_id])).results;
    const baseline = existing.length === 0;
    const byKey = new Map(existing.map((e) => [`${e.kind}\u0000${e.external_id}`, e]));
    const seenKeys = new Set();
    const added = [];
    // ADR-0100 §1: every observed instance is (or joins) one configuration item; a second discovery of the
    // same normalized key updates that CI and links this row — it never inserts a second identity.
    const linkComponent = async (resource_id, it) => {
        if (it.kind === 'api_key' || !it.provider_key)
            return;
        const region = typeof it.attributes?.region === 'string' ? it.attributes.region : undefined;
        await upsertConfigurationItem(db, {
            workspace_id: input.workspace_id, provider_key: it.provider_key, kind: it.kind, external_id: it.external_id, name: it.name,
            ctx: { ...input.component_ctx, ...(region ? { region } : {}) }, source: { table: 'observed_ai_resources', id: resource_id }, now: input.now,
        });
    };
    for (const it of input.items) {
        if (!it.external_id || !(it.kind in AI_ROLE_BY_KIND))
            continue;
        const key = `${it.kind}\u0000${it.external_id}`;
        if (seenKeys.has(key))
            continue;
        seenKeys.add(key);
        const ai_role = AI_ROLE_BY_KIND[it.kind];
        const attrs = JSON.stringify(it.attributes ?? {}).slice(0, 4000);
        const prior = byKey.get(key);
        if (prior) {
            await db.execute(`UPDATE observed_ai_resources SET name = ?, attributes_json = ?, last_seen_at = ?, gone_at = NULL, ai_role = ?, provider_key = ?, access_channel = ?
          WHERE resource_id = ? AND workspace_id = ?`, [it.name.slice(0, 300), attrs, nowIso, ai_role, it.provider_key || null, it.access_channel || null, prior.resource_id, input.workspace_id]);
            await linkComponent(prior.resource_id, it);
            continue;
        }
        const resource_id = input.newId();
        await db.execute(`INSERT INTO observed_ai_resources (resource_id, workspace_id, connector_id, kind, ai_role, provider_key, access_channel, external_id, name, attributes_json, first_seen_at, last_seen_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [resource_id, input.workspace_id, input.connector_id, it.kind, ai_role, it.provider_key || null, it.access_channel || null,
            it.external_id.slice(0, 500), it.name.slice(0, 300), attrs, nowIso, nowIso]);
        await linkComponent(resource_id, it);
        if (!baseline) {
            added.push({ resource_id, connector_id: input.connector_id, kind: it.kind, ai_role, provider_key: it.provider_key || null,
                access_channel: it.access_channel || null, external_id: it.external_id, name: it.name });
        }
    }
    let gone = 0;
    if (input.complete) {
        const goneComponents = new Set();
        for (const e of existing) {
            if (seenKeys.has(`${e.kind}\u0000${e.external_id}`) || e.gone_at)
                continue;
            await db.execute(`UPDATE observed_ai_resources SET gone_at = ? WHERE resource_id = ? AND workspace_id = ?`, [nowIso, e.resource_id, input.workspace_id]);
            if (e.component_ref_id)
                goneComponents.add(e.component_ref_id);
            gone++;
        }
        // ADR-0100 §1 (automated out): a complete run that covers the CI's (connector, kind) and no longer lists
        // it sets `gone_at` — unless another live observation still carries the same identity.
        for (const componentId of goneComponents)
            await syncComponentPresence(db, input.workspace_id, componentId, input.now);
    }
    return { baseline, added, seen: seenKeys.size, gone };
}
/** Programs whose profiles take significant-change notices (FedRAMP today). */
const SCN_PROGRAMS = programsWhere((m) => m.features.significantChangeNotices).map((m) => m.key);
/**
 * New models / vector stores at a provider a system uses → a component-delta
 * check per system and, with a significant-change program, one draft change.
 */
export async function raiseAiResourceChanges(db, storage, input) {
    const out = { checks: 0, drafts: 0 };
    for (const r of input.added) {
        if (!r.ai_role || !CHANGE_ROLES.has(r.ai_role) || !r.provider_key)
            continue;
        const systems = (await db.query(`SELECT DISTINCT system_id FROM ai_system_components WHERE workspace_id = ? AND provider_key = ? ORDER BY system_id`, [input.workspace_id, r.provider_key])).results.map((s) => s.system_id);
        if (systems.length === 0)
            continue;
        const hit = {
            rule_kind: 'ai_resource_added', purl_canonical: null, default_level: 'warn', ai_class: true,
            detail: { resource_kind: r.kind, ai_role: r.ai_role, provider_key: r.provider_key, access_channel: r.access_channel, resource_name: r.name, external_id: r.external_id },
        };
        for (const system_id of systems) {
            const res = await applyDelta(db, storage, {
                workspaceId: input.workspace_id, systemId: system_id, snapshotId: `observed:${r.resource_id}`, priorSnapshotId: null,
                connectorId: input.connector_id, collectorVersion: 'observed-ai/1.0',
            }, { added: [], removed: [], changed: [], rule_hits: [hit] });
            out.checks += res.checksWritten;
        }
        const profile = SCN_PROGRAMS.length ? (await db.query(
        // d1-in-list: bounded — SCN_PROGRAMS is a static list of program keys
        `SELECT profile_id FROM certification_profiles WHERE workspace_id = ? AND program IN (${SCN_PROGRAMS.map(() => '?').join(', ')}) ORDER BY created_at LIMIT 1`, [input.workspace_id, ...SCN_PROGRAMS])).results[0] : undefined;
        if (!profile)
            continue;
        const sourceRef = `observed:${r.resource_id}`;
        const dup = (await db.query(`SELECT change_id FROM significant_changes WHERE workspace_id = ? AND source_ref = ? LIMIT 1`, [input.workspace_id, sourceRef])).results[0];
        if (dup)
            continue;
        const provider = getProvider(r.provider_key)?.name ?? r.provider_key;
        const isModel = r.ai_role === 'model_ref';
        await createSignificantChange(db, {
            workspace_id: input.workspace_id, profile_id: profile.profile_id, change_id: input.newId('scn'),
            title: `New ${isModel ? 'model' : 'vector store'} at ${provider}: ${r.name}`.slice(0, 300),
            description: `Found by the ${provider} connection on ${input.now.toISOString().slice(0, 10)}. Review it: classify the change, or cancel it if it is not part of the system.`,
            // A new model at a provider already in use is the SCN "add models to an approved AI service" case; a person decides.
            answers: isModel ? { adp_ai_model_existing_service: true } : {},
            source: 'component_delta', source_ref: sourceRef, system_ids: systems, provider_keys: [r.provider_key], ai_model_change: isModel,
            created_by_user_id: input.created_by_user_id, now: input.now,
        });
        out.drafts++;
    }
    return out;
}
const NEW_WINDOW_MS = 30 * 86_400_000;
/** Resources still reported at the providers this system's components use (open changes first, then new, then newest; ≤ 200). */
export async function listSystemAiResources(db, workspace_id, system_id, now = new Date()) {
    // Rows with an open change sort ahead of the limit so an old one never drops off.
    const rows = (await db.query(`SELECT o.resource_id, o.connector_id, o.kind, o.ai_role, o.provider_key, o.access_channel, o.name, o.first_seen_at, o.last_seen_at FROM observed_ai_resources o
      WHERE o.workspace_id = ? AND o.gone_at IS NULL AND o.ai_role IS NOT NULL AND o.provider_key IN (
        SELECT provider_key FROM ai_system_components WHERE workspace_id = o.workspace_id AND system_id = ? AND provider_key IS NOT NULL)
      ORDER BY CASE WHEN EXISTS (SELECT 1 FROM significant_changes s WHERE s.workspace_id = o.workspace_id AND s.source_ref = 'observed:' || o.resource_id
        AND s.status NOT IN ('cancelled', 'verified')) THEN 0 ELSE 1 END, o.first_seen_at DESC, o.name LIMIT 200`, [workspace_id, system_id])).results;
    if (rows.length === 0)
        return [];
    // A connector's baseline = its first observation (those resources are not "new").
    const baselines = new Map((await db.query(`SELECT connector_id, MIN(first_seen_at) AS first FROM observed_ai_resources WHERE workspace_id = ? GROUP BY connector_id`, [workspace_id])).results
        .map((b) => [b.connector_id, b.first]));
    const changes = new Map((await db.query(`SELECT source_ref, change_id, status FROM significant_changes WHERE workspace_id = ? AND status NOT IN ('cancelled', 'verified') AND source_ref LIKE 'observed:%'`, [workspace_id])).results.map((c) => [c.source_ref, c]));
    const dismissals = await listSystemAiResourceDismissals(db, workspace_id, system_id);
    const out = rows.map(({ connector_id, ...r }) => ({
        ...r,
        dismissed: dismissals.has(r.resource_id),
        dismissed_reason: dismissals.get(r.resource_id)?.reason ?? null,
        dismissed_by: dismissals.get(r.resource_id)?.dismissed_by_name ?? dismissals.get(r.resource_id)?.dismissed_by_user_id ?? null,
        dismissed_at: dismissals.get(r.resource_id)?.dismissed_at ?? null,
        is_new: r.first_seen_at > (baselines.get(connector_id) ?? r.first_seen_at) && now.getTime() - Date.parse(r.first_seen_at) <= NEW_WINDOW_MS,
        change_id: changes.get(`observed:${r.resource_id}`)?.change_id ?? null,
        change_status: changes.get(`observed:${r.resource_id}`)?.status ?? null,
    }));
    // Changes nobody has picked up first (oldest first: likeliest overdue), then other open changes, then new, then newest.
    const rank = (r) => r.change_status === 'planned' ? 0 : r.change_id ? 1 : r.is_new ? 2 : 3;
    const newest = (a, b) => a.first_seen_at < b.first_seen_at ? 1 : a.first_seen_at > b.first_seen_at ? -1 : a.name.localeCompare(b.name);
    return out.sort((a, b) => rank(a) - rank(b) || (rank(a) === 0 ? -newest(a, b) : newest(a, b)));
}
//# sourceMappingURL=observed-ai-resource-service.js.map